import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [showFullBio, setShowFullBio] = useState(false);

  // Elise's portrait image
  const portraitImage = "https://customer-assets.emergentagent.com/job_powerfully-age/artifacts/n95jo2mu_IMG_6554.JPEG";

  return (
    <section 
      id="about"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12 bg-bone"
      data-testid="about-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="md:col-span-5 md:sticky md:top-24"
          >
            <div className="img-container aspect-[3/4] relative">
              <img
                src={portraitImage}
                alt="Elise Redfield Portrait"
                className="w-full h-full object-cover img-grayscale"
              />
              {/* Accent border */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-sand/40 -z-10" />
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-7 md:pl-8"
          >
            {/* Experience Badge */}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-heading text-5xl md:text-6xl font-bold text-graphite-dark">45+</span>
              <div className="border-l border-graphite/20 pl-4">
                <p className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50">Years</p>
                <p className="font-body text-sm tracking-wide text-graphite/70">Experience</p>
              </div>
            </div>

            {/* Title */}
            <p className="font-body text-xs tracking-[0.3em] uppercase text-sand-dark mb-3">
              My Journey to Fitness
            </p>
            <h2 
              className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-graphite-dark mb-6"
              data-testid="about-title"
            >
              Who I Am
            </h2>

            {/* Bio Intro */}
            <div className="space-y-6 mb-8">
              <p className="font-body text-base md:text-lg font-light leading-relaxed text-graphite/80">
                With over four decades in the health and fitness industry, my mission is to help women 
                build strength, resilience, and lasting vitality through intelligent, personalized training.
              </p>
              <p className="font-body text-base md:text-lg font-light leading-relaxed text-graphite/70">
                My approach was not shaped by textbooks alone. After experiencing significant health 
                challenges that left many practitioners without answers, I was forced to dig deeper, 
                research extensively, and rethink what truly drives recovery, metabolism, and long-term wellness.
              </p>
              <p className="font-body text-base md:text-lg font-light leading-relaxed text-graphite/70">
                That journey became the foundation of the work I now bring to my clients.
              </p>
            </div>

            {/* What I'm Known For */}
            <div className="mb-8">
              <p className="font-body text-sm tracking-wider uppercase text-graphite-dark mb-4">
                I am known for combining:
              </p>
              <ul className="space-y-3">
                {[
                  "Building lean muscle and improving body composition through personalized strength training.",
                  "Creating sustainable nutrition and lifestyle habits that support energy, vitality, and long-term results.",
                  "Helping women work with their changing physiology instead of fighting against it.",
                  "Navigating fatigue, stress, recovery, and real-world challenges with practical strategies and empathy.",
                  "Improving mobility, flexibility, resilience, and confidence for lifelong health and independence.",
                  "Helping women cut through the confusion and focus on what really works."
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-sand-dark mt-2.5 flex-shrink-0" />
                    <span className="font-body text-base text-graphite/70">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="font-body text-base md:text-lg font-light leading-relaxed text-graphite/70 mb-8">
              Over the years, I have helped a wide range of clients — from high-functioning professionals 
              to individuals facing significant physical limitations — rebuild strength, confidence, and quality of life.
            </p>

            {/* Expandable Full Bio */}
            <AnimatePresence>
              {showFullBio && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                  className="overflow-hidden"
                >
                  {/* Adversity Section */}
                  <div className="border-t border-sand/30 pt-8 mb-8">
                    <h3 className="font-heading text-xl md:text-2xl tracking-wide uppercase text-graphite-dark mb-4">
                      Turning Adversity Into Expertise
                    </h3>
                    <div className="space-y-4">
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        At one point in my own journey, severe fatigue, inflammation, and environmental 
                        toxicity left me barely able to function. Traditional answers fell short, which 
                        pushed me to investigate deeper physiological drivers including heavy metal burden, 
                        mold exposure, mitochondrial health, and autonomic nervous system balance.
                      </p>
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        This experience profoundly shaped how I now evaluate training stress, recovery 
                        capacity, and metabolic resilience in my clients.
                      </p>
                    </div>
                    
                    <p className="font-body text-sm tracking-wider uppercase text-graphite-dark mt-6 mb-4">
                      Because of this lens, I am especially careful about:
                    </p>
                    <ul className="space-y-2">
                      {[
                        "Matching training intensity to recovery capacity",
                        "Protecting adrenal and nervous system health",
                        "Building muscle without unnecessary systemic stress",
                        "Creating sustainable, long-term results"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-sand-dark mt-2 flex-shrink-0" />
                          <span className="font-body text-sm text-graphite/70">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Injury Section */}
                  <div className="border-t border-sand/30 pt-8 mb-8">
                    <h3 className="font-heading text-xl md:text-2xl tracking-wide uppercase text-graphite-dark mb-4">
                      Strength Through Injury and Recovery
                    </h3>
                    <div className="space-y-4">
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        My perspective was further sharpened through significant knee injuries earlier 
                        in my career while I was intentionally evaluating the methodologies of other 
                        trainers as part of my ongoing professional education in the field.
                      </p>
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        During that process, I sustained a complete ACL tear and microfracture after 
                        being placed into an unsafe movement that required surgical repair and left me 
                        unable to walk for three months.
                      </p>
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        That experience forced me to rebuild my strength from the ground up and gave me 
                        invaluable insight into what truly protects — and what can compromise — joint integrity.
                      </p>
                      <p className="font-body text-base font-light leading-relaxed text-graphite/70">
                        Years later, after a nasty slip on ice, I experienced a subsequent ACL tear. I was 
                        evaluated by multiple orthopedic surgeons who ultimately advised against surgery 
                        due to the exceptional stability and strength I had developed through my training approach.
                      </p>
                    </div>

                    <p className="font-body text-sm tracking-wider uppercase text-graphite-dark mt-6 mb-4">
                      These real-world experiences now allow me to guide clients safely through:
                    </p>
                    <ul className="space-y-2">
                      {[
                        "Knee instability",
                        "Post-injury strength rebuilding",
                        "Joint protection strategies",
                        "Long-term structural resilience"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-sand-dark mt-2 flex-shrink-0" />
                          <span className="font-body text-sm text-graphite/70">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Read More Toggle */}
            <button
              onClick={() => setShowFullBio(!showFullBio)}
              className="flex items-center gap-2 font-body text-sm tracking-wider uppercase text-sand-dark hover:text-graphite-dark transition-colors mb-10"
              data-testid="read-more-bio"
            >
              {showFullBio ? (
                <>
                  Read Less
                  <ChevronUp size={18} />
                </>
              ) : (
                <>
                  Read My Full Story
                  <ChevronDown size={18} />
                </>
              )}
            </button>

            {/* Philosophy */}
            <div className="border-l-2 border-sand-dark pl-6">
              <p className="font-accent text-xl md:text-2xl italic text-graphite/80">
                "Muscle is longevity. Recovery is power.<br />
                Strength is the fountain of youth."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
